'use client';

import React, { useEffect, useMemo, useRef, useState } from 'react';

export interface CheckboxDropdownOption {
  value: string;
  label: string;
}

export interface CheckboxDropdownProps {
  /** Set by <Field> so the field's label points at the button. */
  id?: string;
  /** Applied to the button; <Field> passes the control height here. */
  style?: React.CSSProperties;
  options: CheckboxDropdownOption[];
  selected: string[];
  onChange: (selected: string[]) => void;
  /** What "nothing selected" means, e.g. "Pan India". Shown as the first choice and on the button. */
  emptyLabel: string;
  /** Plural noun for the summary once several are picked, e.g. "states". */
  itemNoun: string;
  searchPlaceholder?: string;
}

const MAX_NAMES_ON_BUTTON = 2;

/**
 * A dropdown where any number of options can be ticked. The list opens inline (not in a pop-up
 * layer), so it stays inside a modal and its scroll area. Choosing the "empty" option clears the
 * selection; ticking anything else un-ticks it, since "everywhere" and "these ones" exclude each other.
 * The selection is always reported in the order of ``options``.
 */
export function CheckboxDropdown({
  id,
  style,
  options,
  selected,
  onChange,
  emptyLabel,
  itemNoun,
  searchPlaceholder = 'Search',
}: CheckboxDropdownProps) {
  const [open, setOpen] = useState(false);
  const [query, setQuery] = useState('');
  const wrapperRef = useRef<HTMLDivElement>(null);
  const buttonRef = useRef<HTMLButtonElement>(null);

  useEffect(() => {
    if (!open) return;
    function onPointerDown(event: MouseEvent) {
      if (wrapperRef.current && !wrapperRef.current.contains(event.target as Node)) {
        setOpen(false);
        setQuery('');
      }
    }
    document.addEventListener('mousedown', onPointerDown);
    return () => document.removeEventListener('mousedown', onPointerDown);
  }, [open]);

  function close(returnFocus = false) {
    setOpen(false);
    setQuery('');
    if (returnFocus) buttonRef.current?.focus();
  }

  const selectedSet = useMemo(() => new Set(selected), [selected]);

  const visible = useMemo(() => {
    const needle = query.trim().toLowerCase();
    return needle ? options.filter((o) => o.label.toLowerCase().includes(needle)) : options;
  }, [options, query]);

  function toggle(value: string) {
    const next = new Set(selectedSet);
    if (next.has(value)) next.delete(value);
    else next.add(value);
    onChange(options.filter((o) => next.has(o.value)).map((o) => o.value));
  }

  const names = options.filter((o) => selectedSet.has(o.value)).map((o) => o.label);
  const summary =
    names.length === 0
      ? emptyLabel
      : names.length <= MAX_NAMES_ON_BUTTON
        ? names.join(', ')
        : `${names.length} ${itemNoun} selected`;

  return (
    <div
      ref={wrapperRef}
      style={{ position: 'relative' }}
      onKeyDown={(e) => {
        if (e.key === 'Escape' && open) {
          e.stopPropagation();
          close(true);
        }
      }}
    >
      <button
        ref={buttonRef}
        id={id}
        type="button"
        aria-haspopup="true"
        aria-expanded={open}
        onClick={() => setOpen((o) => !o)}
        style={{
          width: '100%',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          gap: 8,
          padding: '0 10px',
          textAlign: 'left',
          background: 'var(--surface, #fff)',
          border: '1px solid var(--border, #D6DCE1)',
          borderRadius: 3,
          fontSize: 13,
          color: 'var(--text, #1B2733)',
          cursor: 'pointer',
          ...style,
        }}
      >
        <span style={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{summary}</span>
        <span aria-hidden="true">▾</span>
      </button>

      {open && (
        <div
          role="group"
          aria-label={`Choose ${itemNoun}`}
          style={{
            position: 'absolute',
            top: 'calc(100% + 2px)',
            left: 0,
            right: 0,
            zIndex: 5,
            background: 'var(--surface, #fff)',
            border: '1px solid var(--border, #D6DCE1)',
            borderRadius: 3,
            boxShadow: '0 6px 18px rgba(0,0,0,0.15)',
          }}
        >
          <div style={{ padding: 8, borderBottom: '1px solid var(--border, #D6DCE1)' }}>
            <input
              type="search"
              aria-label={`Search ${itemNoun}`}
              placeholder={searchPlaceholder}
              value={query}
              autoFocus
              onChange={(e) => setQuery(e.target.value)}
              style={{ width: '100%', height: 30, padding: '0 8px', fontSize: 13 }}
            />
          </div>

          <div style={{ maxHeight: 240, overflowY: 'auto', padding: '4px 0' }}>
            <OptionRow
              label={emptyLabel}
              checked={selected.length === 0}
              onChange={() => onChange([])}
              emphasised
            />
            {visible.map((option) => (
              <OptionRow
                key={option.value}
                label={option.label}
                checked={selectedSet.has(option.value)}
                onChange={() => toggle(option.value)}
              />
            ))}
            {visible.length === 0 && (
              <div style={{ padding: '8px 12px', fontSize: 12.5, color: 'var(--text-dim, #5B6B7A)' }}>
                No {itemNoun} match “{query.trim()}”.
              </div>
            )}
          </div>

          <div
            style={{
              padding: 8,
              borderTop: '1px solid var(--border, #D6DCE1)',
              display: 'flex',
              justifyContent: 'flex-end',
            }}
          >
            <button
              type="button"
              onClick={() => close(true)}
              style={{
                padding: '5px 14px',
                fontSize: 12.5,
                fontWeight: 600,
                background: 'var(--primary, #96650D)',
                color: '#fff',
                border: 'none',
                borderRadius: 3,
                cursor: 'pointer',
              }}
            >
              Done
            </button>
          </div>
        </div>
      )}
    </div>
  );
}

function OptionRow({
  label,
  checked,
  onChange,
  emphasised = false,
}: {
  label: string;
  checked: boolean;
  onChange: () => void;
  emphasised?: boolean;
}) {
  return (
    <label
      style={{
        display: 'flex',
        alignItems: 'center',
        gap: 8,
        padding: '5px 12px',
        fontSize: 13,
        fontWeight: emphasised ? 600 : 400,
        cursor: 'pointer',
        color: 'var(--text, #1B2733)',
      }}
    >
      <input type="checkbox" checked={checked} onChange={onChange} />
      {label}
    </label>
  );
}

export default CheckboxDropdown;
